import { useState, useEffect, useRef } from "react";
import { fetchAiReport, sendAiChat } from "../utils/api.js";
import styles from "./AiReport.module.css";

const MAX_MESSAGE_CHARS = 500;
const MAX_HISTORY_TURNS = 8;

// The model answers in plain text — paragraphs split by a blank line, "-" for
// bullets. No markdown renderer ships with the app, so stray "#" and "**" from
// a model that ignored the instruction are stripped rather than printed.
function cleanLine(line) {
  return line.replace(/^#+\s*/, "").replace(/\*\*/g, "").trim();
}

function renderLines(lines, keyPrefix) {
  const out = [];
  let bullets = [];

  const flush = () => {
    if (!bullets.length) return;
    out.push(
      <ul key={`${keyPrefix}-list-${out.length}`} className={styles.bullets}>
        {bullets.map((item, index) => <li key={index}>{item}</li>)}
      </ul>
    );
    bullets = [];
  };

  for (const line of lines) {
    if (/^[-*•]\s+/.test(line)) {
      bullets.push(line.replace(/^[-*•]\s+/, ""));
      continue;
    }
    flush();
    out.push(<p key={`${keyPrefix}-p-${out.length}`}>{line}</p>);
  }
  flush();
  return out;
}

function ProseText({ text }) {
  const blocks = String(text || "").split(/\n{2,}/).map((block) => block.trim()).filter(Boolean);
  return blocks.map((block, index) =>
    renderLines(block.split("\n").map(cleanLine).filter(Boolean), String(index))
  );
}

// Said before anything is requested and again after, because the log being
// analyzed usually belongs to someone other than the person reading it — and
// that is also why nothing is sent until the button below is pressed. What
// matters to the reader is that a summary left the server, not which vendor or
// model received it, so neither is named here or in the badge.
function disclosure(report) {
  if (!report) {
    return "Writing this analysis sends a summary of the profile to an external AI provider — weekly totals, muscle split and top exercises. Individual sets, reps and weights never leave the server.";
  }
  if (report.mode === "llm") {
    return "A summary of this profile — weekly totals, muscle split, top exercises — was sent to an external AI provider to write the text below. Individual sets, reps and weights were not.";
  }
  return "Nothing left the server: the text below was written here, from the same summary, by the built-in rules.";
}

// Keyed on the username by the caller, so a different profile remounts this
// with a clean report and an empty thread rather than resetting in an effect.
export default function AiReport({ username }) {
  const [result, setResult] = useState({ status: "idle", report: null, error: "" });
  const [turns, setTurns] = useState([]);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [chatError, setChatError] = useState("");
  const threadEnd = useRef(null);
  const { status, report, error } = result;

  async function handleGenerate() {
    setResult({ status: "loading", report: null, error: "" });
    try {
      setResult({ status: "ready", report: await fetchAiReport(username), error: "" });
    } catch (err) {
      setResult({ status: "error", report: null, error: err.message || "Could not generate the analysis." });
    }
  }

  useEffect(() => {
    if (turns.length) threadEnd.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [turns.length, asking]);

  const chatAvailable = report?.mode === "llm";

  async function handleAsk(event) {
    event.preventDefault();
    const message = question.trim();
    if (!message || asking) return;

    setChatError("");
    setAsking(true);
    setQuestion("");
    const asked = [...turns, { role: "user", content: message }];
    setTurns(asked);

    try {
      const data = await sendAiChat(username, message, asked.slice(-MAX_HISTORY_TURNS - 1, -1));
      setTurns([...asked, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setChatError(err.message || "Could not answer that question.");
    } finally {
      setAsking(false);
    }
  }

  return (
    <div className={styles.panel}>
      <div className={styles.heading}>
        <span className={styles.headLabel}>Analysis</span>
        {report && (
          <span className={report.mode === "llm" ? styles.badge : styles.badgeLocal}>
            {report.mode === "llm" ? "AI-written" : "Built-in rules"}
          </span>
        )}
      </div>

      <p className={styles.disclosure}>{disclosure(report)}</p>

      {status !== "ready" && (
        <button className={styles.generate} type="button" onClick={handleGenerate} disabled={status === "loading"}>
          {status === "loading" ? "Writing the analysis…" : status === "error" ? "Try again" : "Write the analysis"}
        </button>
      )}

      {status === "error" && (
        <div className={styles.error} role="alert">{error}</div>
      )}

      {status === "ready" && (
        <>
          {report.degradedReason === "provider-error" && (
            <div className={styles.notice} role="status">
              The model was unavailable, so this is the built-in report. Try again later for the written one.
            </div>
          )}

          <div className={styles.prose}>
            <ProseText text={report.report} />
          </div>

          <details className={styles.contextDetails}>
            <summary className={styles.contextSummary}>
              {report.mode === "llm" ? "Show exactly what was sent" : "Show the summary this was written from"}
            </summary>
            <pre className={styles.contextJson}>{JSON.stringify(report.context, null, 2)}</pre>
          </details>

          <div className={styles.chat}>
            {turns.map((turn, index) => (
              <div key={index} className={turn.role === "user" ? styles.userTurn : styles.replyTurn}>
                {turn.role === "user" ? turn.content : <ProseText text={turn.content} />}
              </div>
            ))}
            {asking && <div className={styles.replyTurn}>Thinking…</div>}
            <div ref={threadEnd} />

            {chatError && <div className={styles.error} role="alert">{chatError}</div>}

            <form className={styles.composer} onSubmit={handleAsk} noValidate>
              <input
                className={styles.input}
                type="text"
                value={question}
                onChange={(event) => setQuestion(event.target.value.slice(0, MAX_MESSAGE_CHARS))}
                placeholder={chatAvailable ? "Ask about this training data…" : "Follow-up questions need the model"}
                maxLength={MAX_MESSAGE_CHARS}
                autoComplete="off"
                disabled={!chatAvailable || asking}
              />
              <button className={styles.send} type="submit" disabled={!chatAvailable || asking || !question.trim()}>
                Ask
              </button>
            </form>
            <p className={styles.footnote}>
              Questions are answered from the summary above only, and it is not medical advice.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
